import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code, Copy, Check, Eye } from 'lucide-react';

type WidgetTheme = 'light' | 'dark';
type WidgetLang = 'en' | 'fr' | 'de' | 'it';

const WidgetConfiguratorPage: React.FC = () => {
  const { clubId, lang } = useParams<{ clubId: string; lang?: string }>();
  const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || 'en';

  const [theme, setTheme] = useState<WidgetTheme>('light');
  const [widgetLang, setWidgetLang] = useState<WidgetLang>(currentLang);
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  const translations = {
    en: {
      title: 'Embed Widget',
      subtitle: 'Add this club to your website',
      widgetType: 'Widget Type',
      theme: 'Theme',
      language: 'Language',
      light: 'Light',
      dark: 'Dark',
      unified: 'Full Club Widget',
      matches: 'Upcoming Matches',
      teams: 'Team Standings',
      players: 'Player Roster',
      venues: 'Venues',
      copyCode: 'Copy Code',
      copied: 'Copied',
      preview: 'Preview',
      hidePreview: 'Hide Preview',
      embedCode: 'Embed Code'
    },
    fr: {
      title: 'Intégrer le widget',
      subtitle: 'Ajoutez ce club à votre site web',
      widgetType: 'Type de widget',
      theme: 'Thème',
      language: 'Langue',
      light: 'Clair',
      dark: 'Sombre',
      unified: 'Widget complet',
      matches: 'Matchs',
      teams: 'Équipes',
      players: 'Joueurs',
      venues: 'Lieux',
      copyCode: 'Copier le code',
      copied: 'Copié',
      preview: 'Aperçu',
      hidePreview: 'Masquer',
      embedCode: 'Code d\'intégration'
    },
    de: {
      title: 'Widget einbetten',
      subtitle: 'Club auf Website einbetten',
      widgetType: 'Widget-Typ',
      theme: 'Design',
      language: 'Sprache',
      light: 'Hell',
      dark: 'Dunkel',
      unified: 'Vollständiges Widget',
      matches: 'Spiele',
      teams: 'Teams',
      players: 'Spieler',
      venues: 'Orte',
      copyCode: 'Code kopieren',
      copied: 'Kopiert',
      preview: 'Vorschau',
      hidePreview: 'Ausblenden',
      embedCode: 'Einbettungscode'
    },
    it: {
      title: 'Incorpora widget',
      subtitle: 'Aggiungi al tuo sito web',
      widgetType: 'Tipo di widget',
      theme: 'Tema',
      language: 'Lingua',
      light: 'Chiaro',
      dark: 'Scuro',
      unified: 'Widget completo',
      matches: 'Partite',
      teams: 'Squadre',
      players: 'Giocatori',
      venues: 'Luoghi',
      copyCode: 'Copia codice',
      copied: 'Copiato',
      preview: 'Anteprima',
      hidePreview: 'Nascondi',
      embedCode: 'Codice di incorporamento'
    }
  };

  const t = translations[currentLang];

  const generateIframeCode = () => {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      theme,
      lang: widgetLang
    });

    return `<iframe
  src="${baseUrl}/widget/${clubId}?${params.toString()}"
  width="100%"
  height="700"
  frameborder="0"
  scrolling="auto"
  style="border: none; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);"
  title="TTStats Club Widget">
</iframe>`;
  };

  const handleCopy = async () => {
    const code = generateIframeCode();
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getPreviewUrl = () => {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      theme,
      lang: widgetLang
    });
    return `${baseUrl}/widget/${clubId}?${params.toString()}`;
  };

  if (!clubId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Club ID is required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center justify-center gap-3 mb-2">
            <Code className="h-8 w-8 text-blue-600" />
            {t.title}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Configuration */}
          <div className="space-y-6">
            {/* Theme & Language */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <div className="grid grid-cols-2 gap-6">
                {/* Theme */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t.theme}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['light', 'dark'] as WidgetTheme[]).map((themeOption) => (
                      <button
                        key={themeOption}
                        onClick={() => setTheme(themeOption)}
                        className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          theme === themeOption
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {t[themeOption]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Language */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t.language}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['en', 'fr', 'de', 'it'] as WidgetLang[]).map((langOption) => (
                      <button
                        key={langOption}
                        onClick={() => setWidgetLang(langOption)}
                        className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all uppercase ${
                          widgetLang === langOption
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {langOption}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Embed Code */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t.embedCode}
                </label>
                <button
                  onClick={handleCopy}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      {t.copied}
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      {t.copyCode}
                    </>
                  )}
                </button>
              </div>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-gray-100">
                  <code>{generateIframeCode()}</code>
                </pre>
              </div>
            </motion.div>

            {/* Preview Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="lg:hidden"
            >
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="w-full px-4 py-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl shadow-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Eye className="h-4 w-4" />
                {showPreview ? t.hidePreview : t.preview}
              </button>
            </motion.div>
          </div>

          {/* Right Column - Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`${showPreview ? 'block' : 'hidden lg:block'}`}
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 sticky top-6">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                <Eye className="h-4 w-4" />
                {t.preview}
              </h3>
              <div className="border-2 border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <iframe
                  src={getPreviewUrl()}
                  className="w-full"
                  style={{
                    height: '700px',
                    border: 'none'
                  }}
                  title="Widget Preview"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WidgetConfiguratorPage;
